import { useEffect, useMemo, useState } from 'react'
import DateRangePicker from '../../../components/DateRangePicker'
import GanttChart, { STAGE_LABELS, StageKey, TimelineRow } from './GanttChart'
import {
  greenCoffeeLotsApi,
  selectionLotsApi,
  roastingBatchesApi,
  blendingBatchesApi,
  packagingBatchesApi,
} from '../../../services/api'

const MAX_ROWS = 150

interface GreenCoffeeLotRow {
  id: number
  lot_code: string
  status: string
  is_active: boolean
  requested_date: string | null
  received_date: string | null
}

interface SelectionLotRow {
  id: number
  lot_code: string
  status: string
  is_active: boolean
  selection_date: string | null
  source_lot_id: number | null
}

interface ProductionBatchRow {
  id: number
  batch_code: string
  status: string
  is_active: boolean
  requested_date: string | null
  completed_date: string | null
  // Only one of these three is populated, depending on which stage's API
  // response this row came from (roasting/blending/packaging each name
  // their own parent FK differently) -- see resolveLotFamily below.
  source_selection_lot_id?: number | null
  source_roasting_batch_id?: number | null
  source_blending_batch_id?: number | null
}

/** Walks the strictly 1:1 parent chain (purchases -> selection -> roasting ->
 * blending -> packaging -- a tree that only fans out, never merges, per
 * infra/db/012_add_supply_chain_inventory.sql's own header) starting from one
 * green coffee lot (purchase), to find every descendant row across all 4
 * downstream stages. Used to answer "show me the whole Gantt for this one
 * purchase," independent of the date-range filter. */
function resolveLotFamily(
  selectedGreenLotId: number,
  selectionLots: SelectionLotRow[],
  roastingBatches: ProductionBatchRow[],
  blendingBatches: ProductionBatchRow[],
  packagingBatches: ProductionBatchRow[]
) {
  const matchedSelection = selectionLots.filter((l) => l.source_lot_id === selectedGreenLotId)
  const selectionIds = new Set(matchedSelection.map((l) => l.id))

  const matchedRoasting = roastingBatches.filter(
    (b) => b.source_selection_lot_id != null && selectionIds.has(b.source_selection_lot_id)
  )
  const roastingIds = new Set(matchedRoasting.map((b) => b.id))

  const matchedBlending = blendingBatches.filter(
    (b) => b.source_roasting_batch_id != null && roastingIds.has(b.source_roasting_batch_id)
  )
  const blendingIds = new Set(matchedBlending.map((b) => b.id))

  const matchedPackaging = packagingBatches.filter(
    (b) => b.source_blending_batch_id != null && blendingIds.has(b.source_blending_batch_id)
  )

  return { matchedSelection, matchedRoasting, matchedBlending, matchedPackaging }
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function isoDateDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Builds the gray "Espera" row for a lot/batch that's done with its current
 * stage but has no child yet in the next one -- spans from the date it
 * became ready to today, so the empty stretch between stages is visible
 * instead of silently invisible between two colored bars. */
function buildWaitRow(id: string, code: string, isActive: boolean, originStageKey: StageKey, waitStart: Date, today: Date): TimelineRow {
  return {
    id,
    stageKey: 'espera',
    code,
    status: 'esperando',
    isActive,
    isPointEvent: false,
    isOngoing: true,
    startDate: waitStart,
    endDate: today,
    label: `Espera tras ${STAGE_LABELS[originStageKey]} · ${code}`,
  }
}

/**
 * Normalizes each stage's list response into TimelineRow[]. Rows with no
 * usable start date are dropped -- they can't be placed on the timeline
 * (defensive; shouldn't happen for well-formed data, but list fields are
 * nullable per the API). Also synthesizes "Espera" rows for anything that's
 * finished its own stage but hasn't advanced to the next one yet -- see
 * buildWaitRow. Packaging has no Espera of its own: once a batch completes,
 * its stock pools into products.stock_kg with no link back to any specific
 * sale, so "time waiting to be sold" isn't something this data can answer
 * (same limitation noted for the per-supplier sell-through question).
 */
function normalizeRows(
  greenCoffeeLots: GreenCoffeeLotRow[],
  selectionLots: SelectionLotRow[],
  roastingBatches: ProductionBatchRow[],
  blendingBatches: ProductionBatchRow[],
  packagingBatches: ProductionBatchRow[]
): TimelineRow[] {
  const today = new Date()
  const rows: TimelineRow[] = []

  const selectedFromGreenLotIds = new Set(
    selectionLots.filter((l) => l.source_lot_id != null).map((l) => l.source_lot_id as number)
  )
  const roastedFromSelectionLotIds = new Set(
    roastingBatches.filter((b) => b.source_selection_lot_id != null).map((b) => b.source_selection_lot_id as number)
  )
  const blendedFromRoastingBatchIds = new Set(
    blendingBatches.filter((b) => b.source_roasting_batch_id != null).map((b) => b.source_roasting_batch_id as number)
  )
  const packagedFromBlendingBatchIds = new Set(
    packagingBatches.filter((b) => b.source_blending_batch_id != null).map((b) => b.source_blending_batch_id as number)
  )

  greenCoffeeLots.forEach((lot) => {
    const start = toDate(lot.requested_date) || toDate(lot.received_date)
    if (!start) return
    const receivedDate = toDate(lot.received_date)
    const isOngoing = !receivedDate
    rows.push({
      id: `purchases-${lot.id}`,
      stageKey: 'purchases',
      code: lot.lot_code,
      status: lot.status,
      isActive: lot.is_active,
      isPointEvent: false,
      isOngoing,
      startDate: start,
      endDate: receivedDate || today,
      label: `${STAGE_LABELS.purchases} · ${lot.lot_code}`,
    })
    if (receivedDate && lot.is_active && !selectedFromGreenLotIds.has(lot.id)) {
      rows.push(buildWaitRow(`espera-purchases-${lot.id}`, lot.lot_code, lot.is_active, 'purchases', receivedDate, today))
    }
  })

  selectionLots.forEach((lot) => {
    const start = toDate(lot.selection_date)
    if (!start) return
    rows.push({
      id: `selection-${lot.id}`,
      stageKey: 'selection',
      code: lot.lot_code,
      status: lot.status,
      isActive: lot.is_active,
      isPointEvent: true,
      isOngoing: false,
      startDate: start,
      endDate: start,
      label: `${STAGE_LABELS.selection} · ${lot.lot_code}`,
    })
    if (lot.is_active && !roastedFromSelectionLotIds.has(lot.id)) {
      rows.push(buildWaitRow(`espera-selection-${lot.id}`, lot.lot_code, lot.is_active, 'selection', start, today))
    }
  })

  const chainStageMap: { list: ProductionBatchRow[]; stageKey: StageKey; childIds: Set<number> | null }[] = [
    { list: roastingBatches, stageKey: 'roasting', childIds: blendedFromRoastingBatchIds },
    { list: blendingBatches, stageKey: 'blending', childIds: packagedFromBlendingBatchIds },
    { list: packagingBatches, stageKey: 'packaging', childIds: null },
  ]

  chainStageMap.forEach(({ list, stageKey, childIds }) => {
    list.forEach((batch) => {
      const start = toDate(batch.requested_date) || toDate(batch.completed_date)
      if (!start) return
      const completedDate = toDate(batch.completed_date)
      const isOngoing = !completedDate
      rows.push({
        id: `${stageKey}-${batch.id}`,
        stageKey,
        code: batch.batch_code,
        status: batch.status,
        isActive: batch.is_active,
        isPointEvent: false,
        isOngoing,
        startDate: start,
        endDate: completedDate || today,
        label: `${STAGE_LABELS[stageKey]} · ${batch.batch_code}`,
      })
      if (completedDate && batch.is_active && childIds && !childIds.has(batch.id)) {
        rows.push(buildWaitRow(`espera-${stageKey}-${batch.id}`, batch.batch_code, batch.is_active, stageKey, completedDate, today))
      }
    })
  })

  return rows.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
}

/** Caps total rows to MAX_ROWS, distributing the cap across stages instead of
 * truncating in array order (which could zero out low-volume stages). Keeps
 * each stage's most recent rows first. */
function capRows(rows: TimelineRow[]): { visible: TimelineRow[]; total: number } {
  if (rows.length <= MAX_ROWS) return { visible: rows, total: rows.length }

  const byStage: Record<StageKey, TimelineRow[]> = {
    purchases: [],
    selection: [],
    roasting: [],
    blending: [],
    packaging: [],
    espera: [],
  }
  rows.forEach((r) => byStage[r.stageKey].push(r))
  const allStageKeys = Object.keys(byStage) as StageKey[]
  allStageKeys.forEach((key) => {
    byStage[key].sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
  })

  const activeStages = allStageKeys.filter((k) => byStage[k].length > 0)
  const share = Math.floor(MAX_ROWS / (activeStages.length || 1))
  const taken: Record<StageKey, number> = { purchases: 0, selection: 0, roasting: 0, blending: 0, packaging: 0, espera: 0 }
  let remaining = MAX_ROWS

  activeStages.forEach((key) => {
    const take = Math.min(share, byStage[key].length)
    taken[key] = take
    remaining -= take
  })

  // Distribute leftover capacity (from stages that had fewer rows than their share)
  // to stages that still have more rows available.
  let progress = true
  while (remaining > 0 && progress) {
    progress = false
    for (const key of activeStages) {
      if (remaining <= 0) break
      if (taken[key] < byStage[key].length) {
        taken[key] += 1
        remaining -= 1
        progress = true
      }
    }
  }

  const visible = activeStages
    .flatMap((key) => byStage[key].slice(0, taken[key]))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())

  return { visible, total: rows.length }
}

export default function TimelineTab() {
  const [startDate, setStartDate] = useState(isoDateDaysAgo(30))
  const [endDate, setEndDate] = useState(todayIso())
  const [greenCoffeeLots, setGreenCoffeeLots] = useState<GreenCoffeeLotRow[]>([])
  const [selectionLots, setSelectionLots] = useState<SelectionLotRow[]>([])
  const [roastingBatches, setRoastingBatches] = useState<ProductionBatchRow[]>([])
  const [blendingBatches, setBlendingBatches] = useState<ProductionBatchRow[]>([])
  const [packagingBatches, setPackagingBatches] = useState<ProductionBatchRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [hiddenStages, setHiddenStages] = useState<Set<StageKey>>(new Set())
  // '' = ver todas las etapas en el rango de fechas (comportamiento de
  // siempre); un id = ver el árbol completo de ESA compra, sin importar el
  // rango de fechas -- "necesitamos ver todo el gantt desde cada compra".
  const [selectedLotId, setSelectedLotId] = useState<number | ''>('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')

    Promise.all([
      greenCoffeeLotsApi.list(),
      selectionLotsApi.list(),
      roastingBatchesApi.list(),
      blendingBatchesApi.list(),
      packagingBatchesApi.list(),
    ])
      .then(([greenRes, selectionRes, roastingRes, blendingRes, packagingRes]) => {
        if (cancelled) return
        setGreenCoffeeLots(greenRes.data.lots || [])
        setSelectionLots(selectionRes.data.lots || [])
        setRoastingBatches(roastingRes.data.batches || [])
        setBlendingBatches(blendingRes.data.batches || [])
        setPackagingBatches(packagingRes.data.batches || [])
      })
      .catch(() => {
        if (!cancelled) setError('No se pudo cargar la línea de tiempo de producción. Intenta recargar la página.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const sortedGreenCoffeeLots = useMemo(
    () =>
      [...greenCoffeeLots].sort((a, b) => {
        const aDate = a.requested_date || ''
        const bDate = b.requested_date || ''
        return bDate.localeCompare(aDate)
      }),
    [greenCoffeeLots]
  )

  const allRows = useMemo(() => {
    if (selectedLotId === '') {
      return normalizeRows(greenCoffeeLots, selectionLots, roastingBatches, blendingBatches, packagingBatches)
    }
    const selectedLot = greenCoffeeLots.filter((l) => l.id === selectedLotId)
    const { matchedSelection, matchedRoasting, matchedBlending, matchedPackaging } = resolveLotFamily(
      selectedLotId,
      selectionLots,
      roastingBatches,
      blendingBatches,
      packagingBatches
    )
    return normalizeRows(selectedLot, matchedSelection, matchedRoasting, matchedBlending, matchedPackaging)
  }, [selectedLotId, greenCoffeeLots, selectionLots, roastingBatches, blendingBatches, packagingBatches])

  const toggleStage = (key: StageKey) => {
    setHiddenStages((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const { visible: cappedRows, total: totalInRange } = useMemo(() => {
    // Con un lote de compra seleccionado se muestra su árbol completo sin
    // recortar por fecha -- el punto es ver todo el recorrido desde esa
    // compra, aunque haya pasado fuera de la ventana de 30 días por defecto.
    if (selectedLotId !== '') return capRows(allRows)

    const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : null
    const rangeEnd = endDate ? new Date(`${endDate}T23:59:59`) : null
    const inRange = allRows.filter((r) => {
      if (rangeStart && r.startDate < rangeStart) return false
      if (rangeEnd && r.startDate > rangeEnd) return false
      return true
    })
    return capRows(inRange)
  }, [allRows, startDate, endDate, selectedLotId])

  const visibleRows = useMemo(
    () => cappedRows.filter((r) => !hiddenStages.has(r.stageKey)),
    [cappedRows, hiddenStages]
  )

  const wasTruncated = cappedRows.length < totalInRange

  return (
    <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-medium text-espresso-800">Línea de Tiempo de Producción</h3>
          <p className="text-sm text-espresso-500 mt-1">
            Vista Gantt de las 5 etapas de la cadena de producción, por fecha de inicio.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="timeline-lot" className="block text-xs font-medium text-espresso-500 mb-1">
              Lote de compra
            </label>
            <select
              id="timeline-lot"
              value={selectedLotId}
              onChange={(e) => setSelectedLotId(e.target.value ? Number(e.target.value) : '')}
              className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
            >
              <option value="">Todos los lotes (por rango de fechas)</option>
              {sortedGreenCoffeeLots.map((lot) => (
                <option key={lot.id} value={lot.id}>
                  {lot.lot_code}
                  {lot.requested_date ? ` — ${lot.requested_date}` : ''}
                </option>
              ))}
            </select>
          </div>
          {selectedLotId === '' && (
            <DateRangePicker
              idPrefix="timeline"
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-espresso-500">Cargando...</div>
      ) : error ? (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm" role="alert">
          {error}
        </div>
      ) : (
        <div>
          {selectedLotId !== '' && (
            <p className="text-xs text-espresso-500 mb-2">
              Mostrando todo el recorrido de este lote de compra, sin importar la fecha —{' '}
              <button type="button" className="underline hover:text-espresso-700" onClick={() => setSelectedLotId('')}>
                quitar filtro
              </button>
              .
            </p>
          )}
          {wasTruncated && (
            <p className="text-xs text-espresso-500 mb-2">
              Mostrando {cappedRows.length} de {totalInRange} registros en este rango — ajusta la ventana de fechas.
            </p>
          )}
          {selectedLotId !== '' && allRows.length <= 1 && (
            <p className="text-sm text-espresso-500 py-4">
              Este lote todavía no avanzó a ninguna otra etapa de la cadena.
            </p>
          )}
          <GanttChart rows={visibleRows} hiddenStages={hiddenStages} onToggleStage={toggleStage} />
        </div>
      )}
    </div>
  )
}
